import { useState } from 'react';
import { Wrench, Plus, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { humanise, relative } from '@/lib/format';
import {
  Badge, Button, Card, EmptyState, Field, Input, Modal, PageHeader,
  Select, Textarea, useToast, statusTone,
} from '@/components/ui';
import { usePortal } from './PortalShell';

const CATEGORIES = ['general', 'plumbing', 'electrical', 'appliance', 'structural', 'pest', 'security', 'cleaning'];

const PRIORITY_HINT: Record<string, string> = {
  low: 'Can wait — cosmetic or minor',
  normal: 'Should be looked at soon',
  high: 'Affecting daily use of the property',
  urgent: 'Unsafe, or causing damage right now',
};

export default function PortalMaintenance() {
  const { data, reload } = usePortal();
  const [reporting, setReporting] = useState(false);

  const open = data.maintenance.filter((m) => ['open', 'acknowledged', 'in_progress'].includes(m.status));
  const closed = data.maintenance.filter((m) => !['open', 'acknowledged', 'in_progress'].includes(m.status));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Repairs"
        subtitle="Report a problem and follow what happens next"
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setReporting(true)}>
            Report a problem
          </Button>
        }
      />

      {data.maintenance.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Wrench className="h-6 w-6" />}
            title="Nothing reported"
            message="If something breaks or needs attention, let your landlord know here — you will be able to see progress."
            action={<Button variant="primary" size="sm" onClick={() => setReporting(true)}>Report a problem</Button>}
          />
        </Card>
      ) : (
        <>
          {open.length > 0 && (
            <div className="space-y-3">
              <p className="label">In progress</p>
              {open.map((m) => (
                <Card key={m.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold">{m.title}</p>
                      {m.description && (
                        <p className="text-sm text-muted mt-1.5 leading-relaxed">{m.description}</p>
                      )}
                      <p className="text-xs text-faint mt-2">
                        {humanise(m.category)} · reported {relative(m.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                      <Badge tone={statusTone(m.priority)}>{humanise(m.priority)}</Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {closed.length > 0 && (
            <div className="space-y-3">
              <p className="label">Finished</p>
              {closed.map((m) => (
                <Card key={m.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold">{m.title}</p>
                      {m.resolution && (
                        <p className="flex items-start gap-1.5 text-sm text-ok mt-2 leading-relaxed">
                          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                          {m.resolution}
                        </p>
                      )}
                      <p className="text-xs text-faint mt-2">
                        {m.resolved_at ? `Resolved ${relative(m.resolved_at)}` : humanise(m.status)}
                      </p>
                    </div>
                    <Badge tone={statusTone(m.status)} dot>{humanise(m.status)}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {reporting && (
        <ReportModal onClose={() => setReporting(false)} onDone={() => { setReporting(false); reload(); }} />
      )}
    </div>
  );
}

function ReportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');

  async function submit() {
    if (!title.trim()) { toast.error('Describe the problem in a few words'); return; }
    setBusy(true);
    try {
      await api.post('/portal/maintenance', {
        title: title.trim(),
        description: description || null,
        category,
        priority,
      });
      toast.success('Reported — your landlord has been notified in the system');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send that report');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Report a problem"
      subtitle="Your landlord will see this straight away"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Send report</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="What is the problem?" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Kitchen tap is dripping"
            autoFocus
          />
        </Field>

        <Field label="Tell us more" hint="When it started, how bad it is, anything you have tried">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="The cold tap has dripped for about a week and is getting worse."
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{humanise(c)}</option>)}
            </Select>
          </Field>
          <Field label="How urgent?" hint={PRIORITY_HINT[priority]}>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {['low', 'normal', 'high', 'urgent'].map((p) => (
                <option key={p} value={p}>{humanise(p)}</option>
              ))}
            </Select>
          </Field>
        </div>

        <p className="text-sm text-muted bg-raised border border-line rounded-xl px-3 py-2.5 leading-relaxed">
          If this is an emergency — gas, flooding, or anything unsafe — call your landlord
          directly rather than waiting for a reply here.
        </p>
      </div>
    </Modal>
  );
}
